/* Robust Excel import + canonical formatting (duration hh:mm:ss, distance km) */
import React, { useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

import iconRun from "../../assets/icons/running.png";
import iconBike from "../../assets/icons/cycling.png";
import iconSwim from "../../assets/icons/swim.png";
import iconStrength from "../../assets/icons/strength.png";
import iconWalk from "../../assets/icons/walk.png";
import iconRest from "../../assets/icons/dayoff.png";
import iconOther from "../../assets/icons/other.png";
import { useTranslation } from "../../i18n";
import API_BASE_URL from '../../config/api';

const API_BASE = `${API_BASE_URL.replace(/\/+$/, '')}/api`;

/* ===== utils ===== */
const two = (n) => (n < 10 ? `0${n}` : String(n));
const isNum = (v) => typeof v === "number" && Number.isFinite(v);

function minutesToHMS(min) {
  const m = Math.max(0, Math.round(min || 0));
  return `${two(Math.floor(m / 60))}:${two(m % 60)}:00`;
}

/** Soporta:
 * - número fracción de día (0.041666… = 1h)
 * - número en minutos (60)
 * - "hh:mm:ss"
 * - "1h 30m"
 * - "60" o "0.041666..." como string
 */
function normalizeDurationFromAny(v) {
  if (v == null || v === "") return "";

  if (isNum(v)) {
    if (v > 0 && v < 2) return minutesToHMS(Math.round(v * 24 * 60));
    if (v <= 24 * 60) return minutesToHMS(v);
    return "";
  }

  const s = String(v).trim();

  const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (m) return `${two(+m[1])}:${two(+m[2])}:${two(m[3] ? +m[3] : 0)}`;

  const H = s.match(/(\d+)\s*h/i);
  const M = s.match(/(\d+)\s*m/i);
  if (H || M) return minutesToHMS((H ? +H[1] * 60 : 0) + (M ? +M[1] : 0));

  const n = Number(s.replace(",", "."));
  if (Number.isFinite(n) && n >= 0) {
    if (n > 0 && n < 2) return minutesToHMS(Math.round(n * 24 * 60));
    if (n <= 24 * 60) return minutesToHMS(n);
  }

  return "";
}

function normalizeKmFromAny(v) {
  if (v == null || v === "") return "";
  if (typeof v === "number") {
    if (v >= 0 && v <= 1000) return String(v);
    return "";
  }
  const s = String(v)
    .toLowerCase()
    .trim()
    .replace("kilómetros", "")
    .replace("kilometros", "")
    .replace("kms", "")
    .replace("km", "")
    .replace(",", ".");
  const num = s.replace(/[^0-9.-]+/g, "");
  const n = Number(num);
  return Number.isFinite(n) && n >= 0 && n <= 1000 ? String(n) : "";
}

const canonicalValue = (metric, v) => {
  if (metric === "duration") return normalizeDurationFromAny(v);
  if (metric === "distance") return normalizeKmFromAny(v);
  return String(v ?? "");
};

const labelForSport = (s) => {
  const x = String(s || "").toLowerCase();
  if (/run|corr/.test(x)) return "Correr";
  if (/cicl|bike/.test(x)) return "Ciclismo";
  if (/nad|swim/.test(x)) return "Nadar";
  if (/fuerza|strength|gym/.test(x)) return "Fuerza";
  if (/rest|descans/.test(x)) return "Descanso";
  if (/walk|camin/.test(x)) return "Caminar";
  return "Otro";
};

const displayTitle = (a) => {
  const titleText = typeof a.title === "string" ? a.title.trim() : "";
  return titleText && titleText !== "(sin título)" ? titleText : labelForSport(a?.sport);
};

/* ===== UI catalogs ===== */
const SPORTS = [
  { value: "running", label: "Correr", icon: iconRun, bg: "#7b2cbf" },
  { value: "cycling", label: "Ciclismo", icon: iconBike, bg: "#2ea44f" },
  { value: "swimming", label: "Nadar", icon: iconSwim, bg: "#00a8e8" },
  { value: "rest", label: "Descanso", icon: iconRest, bg: "#00bcd4" },
  { value: "strength", label: "Fuerza", icon: iconStrength, bg: "#6c2bd9" },
  { value: "walking", label: "Caminar", icon: iconWalk, bg: "#ffa000" },
  { value: "other", label: "Otro", icon: iconOther, bg: "#374151" },
];

const sportInfo = (v) => SPORTS.find((s) => s.value === v) || SPORTS[0];

const getMetrics = (t) => [
  { value: "duration", label: t("cycleTemplatesPanel.metricDuration") },
  { value: "distance", label: t("cycleTemplatesPanel.metricDistance") },
  { value: "elevation", label: t("cycleTemplatesPanel.metricElevation") },
  { value: "tss", label: t("cycleTemplatesPanel.metricTss") },
];

/* ===== sport inference ===== */
function inferSport(title, desc, fallback = "running") {
  const blob = `${title || ""} ${desc || ""}`.toLowerCase();
  if (/\bnad(a|ar|o)\b|swim/.test(blob)) return "swimming";
  if (/\bfuerza|gym|strength/.test(blob)) return "strength";
  if (/\b(bike|cicl|rodillo)\b/.test(blob)) return "cycling";
  if (/\bdescanso|rest\b/.test(blob)) return "rest";
  if (/\bcaminar|caminata|walk\b/.test(blob)) return "walking";
  return fallback;
}

/* ===== small modal ===== */
function SportPicker({ open, value, onPick, onClose }) {
  const { t } = useTranslation();

  if (!open) return null;

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100"
      style={{ background: "rgba(0,0,0,.4)", zIndex: 2000 }}
      onClick={onClose}
    >
      <div
        className="position-absolute start-50 top-50 translate-middle"
        style={{
          width: 380,
          maxWidth: "95vw",
          background: "#fff",
          borderRadius: 8,
          boxShadow: "0 10px 30px rgba(0,0,0,.2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
          <h5 className="m-0">{t("activitySelector.selectActivityType")}</h5>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="p-3 d-grid" style={{ gap: 10 }}>
          {SPORTS.map((s) => (
            <button
              key={s.value}
              type="button"
              className="w-100 d-flex align-items-center justify-content-between text-white"
              style={{ background: s.bg, border: "none", borderRadius: 8, padding: "10px 14px" }}
              onClick={() => {
                onPick(s.value);
                onClose();
              }}
            >
              <span className="d-flex align-items-center gap-3">
                <img
                  src={s.icon}
                  alt={s.label}
                  width={28}
                  height={28}
                  style={{ objectFit: "contain", filter: "brightness(0) invert(1)" }}
                />
                <strong>{s.label}</strong>
              </span>
              {value === s.value && <span>✓</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ===== component ===== */
export default function CycleTemplateCreator() {
  const { t } = useTranslation();

  const [tpl, setTpl] = useState({
    name: "",
    sport: "running",
    distanceCategory: "",
    level: "intermediate",
    defaultDays: 19,
    notes: "",
  });

  const [sportModal, setSportModal] = useState(false);
  const [acts, setActs] = useState([]);

  const emptyAct = {
    id: null,
    day: 1,
    sport: "running",
    title: "",
    metric: "distance",
    value: "",
    description: "",
  };

  const [form, setForm] = useState(emptyAct);
  const [editId, setEditId] = useState(null);
  const [actSportModal, setActSportModal] = useState(false);

  const grouped = useMemo(() => {
    const map = new Map();
    for (let d = 1; d <= Number(tpl.defaultDays || 0); d++) map.set(d, []);
    for (const a of acts) {
      const d = Number(a.day) || 1;
      if (!map.get(d)) map.set(d, []);
      map.get(d).push(a);
    }
    return map;
  }, [acts, tpl.defaultDays]);

  const resetForm = () => {
    setForm({ ...emptyAct, sport: tpl.sport });
    setEditId(null);
  };

  const isValidDurationHMS = (s) => /^\d{1,2}:\d{2}:\d{2}$/.test(String(s || ""));
  const isValidKm = (s) => /^(\d+|\d+\.\d+)$/.test(String(s || ""));

  /* add / edit with canonical value */
  const addAct = () => {
    if (!form.description.trim()) return alert(t("cycleTemplatesPanel.validationDescription"));
    if (form.value === "") return alert(t("cycleTemplatesPanel.validationPlannedRequired"));

    const val = canonicalValue(form.metric, form.value);
    if (form.metric === "duration" && !isValidDurationHMS(val)) {
      return alert(t("cycleTemplatesPanel.validationDuration"));
    }
    if (form.metric === "distance" && !isValidKm(val)) {
      return alert(t("cycleTemplatesPanel.validationDistance"));
    }

    const id = Date.now().toString(36);
    const fixedTitle = form.title.trim() || labelForSport(form.sport);
    setActs((prev) => [...prev, { ...form, title: fixedTitle, value: val, id }]);
    resetForm();
  };

  const startEdit = (id) => {
    const a = acts.find((x) => x.id === id);
    if (!a) return;
    setEditId(id);
    setForm({ ...a });
  };

  const saveEdit = () => {
    if (!form.description.trim() || form.value === "") return alert(t("cycleTemplatesPanel.completeTitleDescriptionValue"));
    const val = canonicalValue(form.metric, form.value);
    if (form.metric === "duration" && !isValidDurationHMS(val)) {
      return alert(t("cycleTemplatesPanel.validationDuration"));
    }
    if (form.metric === "distance" && !isValidKm(val)) {
      return alert(t("cycleTemplatesPanel.validationDistance"));
    }
    const fixedTitle = form.title.trim() || labelForSport(form.sport);
    setActs((prev) => prev.map((x) => (x.id === editId ? { ...form, title: fixedTitle, value: val } : x)));
    resetForm();
  };

  const removeAct = (id) => setActs((prev) => prev.filter((x) => x.id !== id));

  /* ===== Importar Excel por posición real de columnas ===== */
  const handleExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];

      const rowsArr = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: "" });
      if (!rowsArr.length) throw new Error(t("cycleTemplatesPanel.alerts.excelNoRows"));

      const headers = rowsArr[0].map((h) => String(h || "").trim());

      const idxOf = (patterns) => {
        const pats = patterns.map((p) => p.toLowerCase());
        for (let i = 0; i < headers.length; i++) {
          const low = headers[i].toLowerCase();
          if (pats.some((p) => low.includes(p))) return i;
        }
        return -1;
      };

      const idxDay = idxOf(["día", "dia", "day", "calendario"]);
      const idxSport = idxOf(["deporte", "sport"]);
      const idxTitle = idxOf(["título", "titulo", "tarea", "sesión", "sesion", "tipo de activid", "actividad", "nombre", "resumen"]);
      const idxDesc = idxOf(["descripción", "descripcion", "detalle"]);
      const idxPlan = idxOf(["planificado", "planificada", "planificacion", "planificación"]);

      const parsed = [];

      for (let r = 1; r < rowsArr.length; r++) {
        const row = rowsArr[r];

        const day = idxDay >= 0 ? Number(row[idxDay]) || 1 : 1;
        const desc = idxDesc >= 0 ? String(row[idxDesc] || "").trim() : "";
        let sport = idxSport >= 0 ? String(row[idxSport] || "").toLowerCase() : "";
        let title = idxTitle >= 0 ? String(row[idxTitle] || "").trim() : "";

        const planStr = idxPlan >= 0 ? String(row[idxPlan] || "").toLowerCase() : "";
        const rightIdx = idxPlan >= 0 ? idxPlan + 1 : -1;
        const rawRight = rightIdx < row.length ? row[rightIdx] : "";

        let metric = "distance";
        if (/durac|tiemp|min/.test(planStr)) metric = "duration";
        if (/dist|km/.test(planStr)) metric = "distance";

        let value = "";
        if (metric === "duration") {
          value =
            normalizeDurationFromAny(rawRight) ||
            normalizeDurationFromAny(title) ||
            normalizeDurationFromAny(desc);
        } else {
          value =
            normalizeKmFromAny(rawRight) ||
            normalizeKmFromAny(title) ||
            normalizeKmFromAny(desc);
        }

        if (!sport) sport = inferSport(title, desc, "running");
        else {
          if (/corre|run/.test(sport)) sport = "running";
          else if (/cicl|bike/.test(sport)) sport = "cycling";
          else if (/nad|swim/.test(sport)) sport = "swimming";
          else if (/fuerza|strength/.test(sport)) sport = "strength";
          else if (/descanso|rest/.test(sport)) sport = "rest";
          else if (/camin|walk/.test(sport)) sport = "walking";
          else sport = "other";
        }

        if (!title) title = labelForSport(sport);

        parsed.push({
          id: cryptoRandom(),
          day,
          sport,
          title,
          metric,
          value,
          description: desc,
        });
      }

      setActs(
        parsed.map((a) => ({
          ...a,
          title: displayTitle(a),
          value: canonicalValue(a.metric, a.value),
        }))
      );

      const maxDay = parsed.reduce((m, a) => Math.max(m, a.day), 1);
      setTpl((prev) => ({ ...prev, defaultDays: maxDay }));
      alert(`Importadas ${parsed.length} actividades desde el Excel.`);
    } catch (err) {
      console.error(err);
      alert(t("cycleTemplatesPanel.excelReadError"));
    } finally {
      e.target.value = "";
    }
  };

  /* ===== Guardar plantilla ===== */
  const handleSave = async () => {
    if (!tpl.name.trim()) return alert(t("cycleTemplatesPanel.planNameRequired"));
    if ((Number(tpl.defaultDays) || 0) <= 0) return alert(t("cycleTemplatesPanel.totalDays"));
    if (acts.length === 0) return alert(t("cycleTemplatesPanel.addAtLeastOneActivityToTemplate"));

    for (const a of acts) {
      if (a.metric === "duration" && !/^\d{1,2}:\d{2}:\d{2}$/.test(a.value)) {
        alert(t("cycleTemplatesPanel.validationDuration"));
        return;
      }
      if (a.metric === "distance" && !/^(\d+|\d+\.\d+)$/.test(a.value)) {
        alert(t("cycleTemplatesPanel.validationDistance"));
        return;
      }
    }

    const days = [];
    grouped.forEach((list, dayNumber) => {
      if (list.length === 0) return;
      days.push({
        day: dayNumber,
        activities: list.map((a) => ({
          title: (a.title && a.title.trim()) || labelForSport(a.sport),
          sport: a.sport,
          plannedType: a.metric,
          plannedValue: String(a.value),
          description: (a.description || "").trim(),
        })),
      });
    });

    const activities = days.flatMap((d) =>
      (d.activities || []).map((act) => ({
        day: d.day,
        ...act,
      }))
    );

    const body = {
      name: tpl.name,
      sport: tpl.sport,
      distanceCategory: tpl.distanceCategory,
      level: tpl.level,
      defaultDays: Number(tpl.defaultDays),
      notes: tpl.notes,
      days,
      activities,
    };

    try {
      await axios.post(`${API_BASE}/cycles/templates`, body);
      alert(t("cycleTemplatesPanel.templateCreated"));
      setTpl({
        name: "",
        sport: "running",
        distanceCategory: "",
        level: "intermediate",
        defaultDays: 19,
        notes: "",
      });
      setActs([]);
      setForm({ ...emptyAct, sport: "running" });
      setEditId(null);
    } catch (e) {
      console.error(e);
      alert(t("cycleTemplatesPanel.templateSaveError"));
    }
  };

  return (
    <div className="p-3" style={{ maxWidth: 980, margin: "0 auto" }}>
      <div className="card shadow-sm">
        <div className="card-body">
          <h5 className="card-title mb-3">Crear nueva plantilla</h5>

          <div className="d-flex flex-wrap gap-2">
            <input
              className="form-control"
              style={{ minWidth: 170 }}
              placeholder="Nombre del plan"
              value={tpl.name}
              onChange={(e) => setTpl({ ...tpl, name: e.target.value })}
            />
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center justify-content-between"
              onClick={() => setSportModal(true)}
            >
              <span className="d-flex align-items-center gap-2">
                <img src={sportInfo(tpl.sport).icon} alt="" width={16} height={16} />
                {sportInfo(tpl.sport).label}
              </span>
              <span className="ms-2">▾</span>
            </button>
            <input
              className="form-control"
              style={{ minWidth: 180 }}
              placeholder="Distancia / categoría"
              value={tpl.distanceCategory}
              onChange={(e) => setTpl({ ...tpl, distanceCategory: e.target.value })}
            />
            <div className="input-group" style={{ width: 110 }}>
              <span className="input-group-text">Av.</span>
              <select
                className="form-select"
                value={tpl.level}
                onChange={(e) => setTpl({ ...tpl, level: e.target.value })}
              >
                <option value="novice">Nov</option>
                <option value="intermediate">Int</option>
                <option value="advanced">Avz</option>
              </select>
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2 mt-2">
            <input
              className="form-control"
              style={{ minWidth: 120, width: 120 }}
              type="number"
              min={1}
              value={tpl.defaultDays}
              onChange={(e) => setTpl({ ...tpl, defaultDays: Number(e.target.value) })}
            />
            <input
              className="form-control"
              placeholder="Notas / objetivo del bloque (opcional)"
              value={tpl.notes}
              onChange={(e) => setTpl({ ...tpl, notes: e.target.value })}
            />
          </div>

          <div className="mt-4 border rounded p-2">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="m-0">Actividades de la plantilla</h6>
              <small className="text-muted">Total actividades: {acts.length}</small>
            </div>

            <div className="row g-2 align-items-end">
              <div className="col-2">
                <label className="form-label mb-1">{t("trainerTemplates.day")}</label>
                <input
                  className="form-control"
                  type="number"
                  min={1}
                  max={Number(tpl.defaultDays || 1)}
                  value={form.day}
                  onChange={(e) => setForm({ ...form, day: Number(e.target.value) })}
                />
              </div>

              <div className="col-3">
                <label className="form-label mb-1">{t("trainerLibraryManager.sport")}</label>
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100 d-flex justify-content-between"
                  onClick={() => setActSportModal(true)}
                >
                  <span className="d-flex align-items-center gap-2">
                    <img src={sportInfo(form.sport).icon} width={16} height={16} alt="" />
                    {sportInfo(form.sport).label}
                  </span>
                  <span>▾</span>
                </button>
              </div>

              <div className="col-7">
                <label className="form-label mb-1">Título / Tarea</label>
                <input
                  className="form-control"
                  {...{placeholder: t("cycleTemplatesPanel.activityTitlePlaceholder")}}
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>

              <div className="col-4">
                <label className="form-label mb-1">{t("cycleTemplatesPanel.metric")}</label>
                <select
                  className="form-select"
                  value={form.metric}
                  onChange={(e) => setForm({ ...form, metric: e.target.value })}
                >
                  {getMetrics(t).map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-3">
                <label className="form-label mb-1">Valor</label>
                <input
                  className="form-control"
                  placeholder="hh:mm:ss o km"
                  value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                />
              </div>

              <div className="col-5">
                <label className="form-label mb-1">{t("trainerLibraryManager.description")}</label>
                <input
                  className="form-control"
                  {...{placeholder: t("cycleTemplatesPanel.activityDescriptionPlaceholder")}}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>

              <div className="col-12 d-flex gap-2">
                {editId ? (
                  <>
                    <button className="btn btn-success" type="button" onClick={saveEdit}>
                      {t("common.save")}
                    </button>
                    <button className="btn btn-outline-secondary" type="button" onClick={resetForm}>
                      {t("common.cancel")}
                    </button>
                  </>
                ) : (
                  <button className="btn btn-primary" type="button" onClick={addAct}>
                    Añadir actividad
                  </button>
                )}
              </div>
            </div>

            <div className="mt-3">
              {[...grouped.keys()].map((day) => {
                const list = grouped.get(day) || [];
                if (list.length === 0) return null;

                return (
                  <div key={day} className="mb-2">
                    <div className="fw-semibold mb-1">Día {day}</div>
                    <div className="list-group">
                      {list.map((a) => (
                        <div
                          key={a.id}
                          className="list-group-item d-flex justify-content-between align-items-center"
                        >
                          <div className="d-flex align-items-center gap-2">
                            <img src={sportInfo(a.sport).icon} alt="" width={20} height={20} />
                            <div>
                              <div className="fw-semibold">{displayTitle(a)}</div>
                              <small className="text-muted">
                                {getMetrics(t).find((m) => m.value === a.metric)?.label}: {canonicalValue(a.metric, a.value)} ·{" "}
                                {a.description}
                              </small>
                            </div>
                          </div>
                          <div className="d-flex gap-2">
                            <button className="btn btn-sm btn-success" onClick={() => startEdit(a.id)}>
                              {t("trainerLibraryManager.edit")}
                            </button>
                            <button className="btn btn-sm btn-danger" onClick={() => removeAct(a.id)}>
                              {t("common.delete")}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-3 d-flex align-items-center gap-2">
            <button className="btn btn-success" onClick={handleSave}>
              {t("cycleTemplatesPanel.createButton")}
            </button>
            <div className="vr" />
            <label className="btn btn-outline-primary mb-0">
              {t("cycleTemplatesPanel.importExcel")}
              <input type="file" accept=".xlsx,.xls" hidden onChange={handleExcel} />
            </label>
            <small className="text-muted ms-2">{t("cycleTemplatesPanel.backendNote")}</small>
          </div>
        </div>
      </div>

      <SportPicker
        open={sportModal}
        value={tpl.sport}
        onPick={(sport) => setTpl({ ...tpl, sport })}
        onClose={() => setSportModal(false)}
      />
      <SportPicker
        open={actSportModal}
        value={form.sport}
        onPick={(sport) => setForm({ ...form, sport })}
        onClose={() => setActSportModal(false)}
      />
    </div>
  );
}

function cryptoRandom() {
  try {
    const b = new Uint32Array(1);
    crypto.getRandomValues(b);
    return b[0].toString(36);
  } catch {
    return Math.random().toString(36).slice(2);
  }
}